import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collections, momentCategories, moments } from "@/mocks/moments";
import { topics } from "@/mocks/topics";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";

const relatedTopics = topics.filter((t) => t.categorySlug === "moment");

export default function Moments() {
  const [activeCategory, setActiveCategory] = useState("全部");

  const visible = useMemo(
    () =>
      activeCategory === "全部" ? moments : moments.filter((m) => m.category === activeCategory),
    [activeCategory]
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="名场面合集 | 瓜田黑料高能瞬间"
        description="瓜田黑料名场面合集，收录舞台救场、竞技反转、综艺翻车、团综高光等被剪进二创最多的高能瞬间，按类别整理，快速补课不迷路。"
        keywords="瓜田黑料名场面,名场面合集,高能瞬间,二创合集"
        path="/moments"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              name: "名场面合集",
              description: "瓜田黑料名场面合集，收录年度高能瞬间与二创素材",
              inLanguage: "zh-CN",
              url: "https://jiemia.cn/moments",
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://jiemia.cn/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "名场面合集",
                  item: "https://jiemia.cn/moments",
                },
              ],
            },
            {
              "@type": "ItemList",
              name: "年度名场面",
              itemListElement: moments.map((m, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: m.title,
                description: m.desc,
              })),
            },
          ],
        }}
      />
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <section className="w-full px-4 md:px-10 pt-8 md:pt-10">
          <Breadcrumb items={[{ name: "首页", path: "/" }, { name: "名场面合集" }]} />
        </section>

        <section className="w-full px-4 md:px-10 pt-6 pb-12">
          <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.18em] text-secondary-700 uppercase">
              <span className="w-4 h-px bg-secondary-400" />
              Famous Moments Archive
            </span>
            <h1 className="mt-3 font-heading font-black text-2xl md:text-4xl text-secondary-950">
              名场面合集 · 年度高能瞬间
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-secondary-900/80">
              从舞台救场到游戏翻车，从一句神来之笔到全场沉默的三秒，这里收录了被反复回放与二创最多的名场面。每个片段都会保留必要语境，避免断章取义，让你看得明白也笑得放心。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 pb-14">
          <div className="flex flex-wrap gap-2">
            {momentCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((moment) => (
              <Link
                key={moment.id}
                to={`/topic/${moment.topicSlug}`}
                className="group rounded-lg overflow-hidden bg-background-100 border border-background-200 flex flex-col hover:border-primary-300 transition-colors"
              >
                <div className="relative w-full aspect-[4/3]">
                  <img
                    src={moment.image}
                    alt={`${moment.title} — 名场面合集图片`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-background-50/90 backdrop-blur text-xs font-medium text-secondary-800">
                    {moment.category}
                  </span>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h2 className="font-heading font-bold text-lg text-foreground-950 group-hover:text-primary-600 transition-colors">
                    {moment.title}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600 flex-1">{moment.desc}</p>
                  <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
                    <span>{moment.year} 年度</span>
                    <span className="text-primary-600 font-medium">
                      <i className="ri-repeat-line mr-1" />
                      回放热度 {moment.heat}%
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {visible.length === 0 && (
            <div className="mt-8 rounded-lg bg-background-100 border border-background-200 p-10 text-center">
              <p className="text-sm text-foreground-600">该分类暂无名场面，换个标签看看。</p>
            </div>
          )}
        </section>

        <section className="w-full px-4 md:px-10 py-14 bg-background-100">
          <SectionHeading
            eyebrow="Collections"
            title="相关合集整理"
            desc="把名场面按主题重新打包，适合一次刷完。"
            actionPath="/rankings"
            actionLabel="回到年度盘点"
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {collections.map((col) => (
              <Link
                key={col.title}
                to={`/topic/${col.topicSlug}`}
                className="group rounded-lg bg-background-50 border border-background-200 p-6 flex flex-col hover:border-primary-300 transition-colors"
              >
                <span className="w-11 h-11 rounded-md bg-accent-100 flex items-center justify-center text-2xl text-accent-700">
                  <i className={col.icon} />
                </span>
                <h3 className="mt-4 font-heading font-bold text-base text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {col.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600 flex-1">{col.desc}</p>
                <span className="mt-4 text-xs text-foreground-500">收录 {col.count} 篇</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-14 bg-background-50">
          <SectionHeading
            eyebrow="Keep Reading"
            title="继续看名场面相关盘点"
            desc="名场面背后的完整故事，都在这些盘点里。"
          />
          <div className="mt-8 rounded-lg bg-background-100 px-5 md:px-7 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {relatedTopics.map((topic) => (
              <Link
                key={topic.slug}
                to={`/topic/${topic.slug}`}
                className="group flex items-start gap-4 rounded-lg bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors"
              >
                <span className="w-10 h-10 rounded-md bg-primary-50 flex items-center justify-center text-xl text-primary-600 flex-shrink-0">
                  <i className="ri-movie-2-line" />
                </span>
                <span>
                  <span className="block font-medium text-sm text-foreground-950 group-hover:text-primary-600 transition-colors">
                    {topic.title}
                  </span>
                  <span className="mt-1 block text-xs text-foreground-500 line-clamp-2">
                    {topic.summary}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}